export const PlanJobStatusKind = {
    VALIDATION_PROCESSING: 'validation-processing',
    VALIDATION_ACTIVE: 'validation-active',
    PROCESSING_FAILED: 'processing-failed',
    VALIDATION_CRITICAL: 'validation-critical',
    VALIDATION_NON_CRITICAL: 'validation-non-critical',
    BOTMARK_ESTIMATE: 'botmark-estimate',
    JOB_READY: 'job-ready',
    JOB_COMPLETE: 'job-complete',
    DOWNLOAD: 'download',
    DOWNLOAD_POLICY: 'download-policy',
    ACCEPT_DOWNLOAD: 'accept-download',
    ACCEPT_DOWNLOAD_POLICY: 'accept-download-policy',
    STORAGE_LIMIT: 'storage-limit',
    LOCKED_COMPANY: 'locked-company',
    WAITING: 'waiting',
    EMPTY: 'empty',
};

function hasActiveCompanyStatus(company, companyData) {
    return company?.companyStatus === 1 || companyData?.companyStatus === 1;
}

function hasLockedCompanyStatus(company, companyData) {
    return company?.companyStatus === 2 || companyData?.companyStatus === 2;
}

function status(sortLabel, kind) {
    return { sortLabel, kind };
}

function downloadStatus(hasAcceptedDownloadPolicy) {
    return hasAcceptedDownloadPolicy
        ? status('Download', PlanJobStatusKind.DOWNLOAD)
        : status('Download Policy', PlanJobStatusKind.DOWNLOAD_POLICY);
}

function acceptDownloadStatus(hasAcceptedDownloadPolicy) {
    return hasAcceptedDownloadPolicy
        ? status('Accept & Download', PlanJobStatusKind.ACCEPT_DOWNLOAD)
        : status('Download Policy', PlanJobStatusKind.ACCEPT_DOWNLOAD_POLICY);
}

function resolveAcceptOrStorageLimit(hasAcceptedDownloadPolicy, fileStorageCount) {
    if (fileStorageCount >= 100) {
        return status('File Storage Limit Reached', PlanJobStatusKind.STORAGE_LIMIT);
    }
    return acceptDownloadStatus(hasAcceptedDownloadPolicy);
}

function hasNoInvoiceJob(row) {
    return row.invoiceJobId == null;
}

/** Shared: pending accept & download, no invoice job yet. */
function isPendingAcceptWithoutInvoiceJob(row) {
    return row.botmarkIsInvoiceAccepted === 2 && hasNoInvoiceJob(row);
}

/** Shared: already accepted for download, no invoice job yet. */
function isAcceptedDownloadWithoutInvoiceJob(row) {
    return row.botmarkIsInvoiceAccepted === 5 && hasNoInvoiceJob(row);
}

function invoiceSentAndAcceptedForDownload(row, allowDownload) {
    return row.invoiceIsSent === true
        && row.isInvoiceAccepted === 5
        && row.botmarkIsInvoiceAccepted === 4
        && row.allowDownload === allowDownload;
}

function processedAcceptedInvoiceSent(row, allowDownload) {
    return row.isProcessed === 2
        && row.isAccepted === 1
        && row.isInvoiceAccepted === 5
        && row.invoiceIsSent === true
        && row.allowDownload === allowDownload;
}

const managerRules = [
    {
        when: (row) => row.isProcessed === 0,
        status: () => status('Validation', PlanJobStatusKind.VALIDATION_PROCESSING),
    },
    {
        when: (row) => row.isAccepted === 0 && row.sequencingErrorCode <= 1,
        status: () => status('Validation', PlanJobStatusKind.VALIDATION_ACTIVE),
    },
    {
        when: (row) => row.isAccepted === 0 && row.sequencingErrorCode === 88,
        status: () => status('Processing Failed', PlanJobStatusKind.PROCESSING_FAILED),
    },
    {
        when: (row) => row.isAccepted === 0 && row.sequencingErrorCode > 1 && row.sequencingErrorCode !== 20,
        status: () => status('Validation', PlanJobStatusKind.VALIDATION_CRITICAL),
    },
    {
        when: (row) => row.isAccepted === 0 && row.sequencingErrorCode === 20,
        status: () => status('Validation', PlanJobStatusKind.VALIDATION_NON_CRITICAL),
    },
    {
        when: (row) => row.isAccepted === 1 && row.botmarkIsInvoiceAccepted === 1,
        status: () => status('Botmark Estimate', PlanJobStatusKind.BOTMARK_ESTIMATE),
    },
    {
        when: (row) => (
            row.botmarkInvoiceIsSent === true
            && row.botmarkIsInvoiceAccepted === 4
            && (row.botmarkCustomerInvoiceStatus !== 3 || row.allowDownload === true)
        ),
        status: () => status('Job Ready', PlanJobStatusKind.JOB_READY),
    },
    {
        when: (row) => (
            invoiceSentAndAcceptedForDownload(row, false)
            || processedAcceptedInvoiceSent(row, false)
        ),
        status: () => status('Job Complete', PlanJobStatusKind.JOB_COMPLETE),
    },
    {
        when: (row) => (
            invoiceSentAndAcceptedForDownload(row, true)
            || processedAcceptedInvoiceSent(row, true)
        ),
        status: (_row, { hasAcceptedDownloadPolicy }) => downloadStatus(hasAcceptedDownloadPolicy),
    },
    {
        when: (row, { company, companyData }) => (
            isPendingAcceptWithoutInvoiceJob(row)
            && hasActiveCompanyStatus(company, companyData)
        ),
        status: (_row, { hasAcceptedDownloadPolicy, fileStorageCount }) => (
            resolveAcceptOrStorageLimit(hasAcceptedDownloadPolicy, fileStorageCount)
        ),
    },
    {
        when: (row, { company, companyData }) => (
            isAcceptedDownloadWithoutInvoiceJob(row)
            && hasActiveCompanyStatus(company, companyData)
        ),
        status: (_row, { hasAcceptedDownloadPolicy }) => downloadStatus(hasAcceptedDownloadPolicy),
    },
    {
        when: (row, { company, companyData }) => (
            isPendingAcceptWithoutInvoiceJob(row)
            && hasLockedCompanyStatus(company, companyData)
        ),
        status: () => status('Locked Company: Contact Botweb', PlanJobStatusKind.LOCKED_COMPANY),
    },
];

const customerRules = [
    {
        when: (row) => (
            row.invoiceIsSent === true
            && row.isInvoiceAccepted === 5
            && row.botmarkIsInvoiceAccepted === 4
        ),
        status: () => status('Job Complete', PlanJobStatusKind.JOB_COMPLETE),
    },
    {
        when: (row, { company, companyData }) => (
            isAcceptedDownloadWithoutInvoiceJob(row)
            && hasActiveCompanyStatus(company, companyData)
        ),
        status: (_row, { hasAcceptedDownloadPolicy }) => downloadStatus(hasAcceptedDownloadPolicy),
    },
    {
        when: (row, { company, companyData }) => (
            isPendingAcceptWithoutInvoiceJob(row)
            && hasActiveCompanyStatus(company, companyData)
        ),
        status: (_row, { hasAcceptedDownloadPolicy, fileStorageCount }) => (
            resolveAcceptOrStorageLimit(hasAcceptedDownloadPolicy, fileStorageCount)
        ),
    },
    {
        when: (row, { company, companyData }) => (
            isPendingAcceptWithoutInvoiceJob(row)
            && hasLockedCompanyStatus(company, companyData)
        ),
        status: () => status('Locked Company: Contact Botweb', PlanJobStatusKind.LOCKED_COMPANY),
    },
];

export function resolvePlanTableJobStatus(row, context) {
    const {
        canManagePlans,
        company,
        companyData,
        hasAcceptedDownloadPolicy = true,
        fileStorageCount = 0,
    } = context;

    const ruleContext = {
        company,
        companyData,
        hasAcceptedDownloadPolicy,
        fileStorageCount,
    };
    const rules = canManagePlans ? managerRules : customerRules;
    const matched = rules.find((rule) => rule.when(row, ruleContext));

    if (matched) {
        return matched.status(row, ruleContext);
    }

    return canManagePlans
        ? status('', PlanJobStatusKind.EMPTY)
        : status('Waiting for Completion', PlanJobStatusKind.WAITING);
}

export function getPlanTableJobStatusSortLabel(row, context) {
    return resolvePlanTableJobStatus(row, context).sortLabel;
}
